import React, { useContext, useState, useMemo, useEffect } from 'react';
import { css } from '@emotion/react';
import { MODALS, TEMP_STOP } from '../constants';
import { PlanningContext } from '../context';
import { GridItemRow, ModalActions, ModalContent, ModalTitle, PrimaryButton, SecondaryButton } from '../blocks';
import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { FormControl, Select, MenuItem } from '@material-ui/core';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { cloneDeep } from 'lodash';
import { UPDATE_ROUTE_STOPS } from '../graphql/mutations';
import { buildUpdateStopsOptimistic, updateStopsCacheHandler } from '../graphql/updateStopsOptimistic';
import { useMutation } from '@apollo/client';
import { toArrayLiteral } from '@/utilities/toArrayLiteral';
import { DISPATCH_PLAN_SERIALIZATION_KEY } from '@/graphql/serializingLink';
import { v4 as uuid } from 'uuid';

const lunchDurations = [
    {
        text: '10 min',
        value: 10,
    },
    {
        text: '15 min',
        value: 15,
    },
    {
        text: '30 min',
        value: 30,
    },
    {
        text: '45 min',
        value: 45,
    },
    {
        text: '60 min',
        value: 60,
    },
    {
        text: '75 min',
        value: 75,
    },
    {
        text: '90 min',
        value: 90,
    },
    {
        text: '105 min',
        value: 105,
    },
    {
        text: '120 min',
        value: 120,
    },
];

const DriverLunchModal = () => {
    const {
        modalOpen,
        setModalOpen,
        selectedRoute,
        setError,
        state: { routes },
    } = useContext(PlanningContext);

    const [lunchDuration, setLunchDuration] = useState(null);
    const [lunchStop, setLunchStop] = useState(null);
    const [previousLunchStop, setPreviousLunchStop] = useState(null);

    const [updateStops, { loading: stopsLoading }] = useMutation(UPDATE_ROUTE_STOPS, {
        // Stop/mapping lists self-heal from update_routes_by_pk's returning
        // snapshot for the ORDERED storage key. applyUpdateStopsResult also
        // evicts deleted stops and maintains the argument-less
        // stopsByRouteId key other pages read.
        context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
        update: updateStopsCacheHandler,
    });

    const handleModalClose = () => {
        setModalOpen(null);
    };

    const route = useMemo(() => {
        return routes?.find((route) => route.route_id === selectedRoute);
    }, [routes, selectedRoute]);

    const ordersByKey = useMemo(() => {
        return Object.fromEntries((route?.orders || []).map((mapping) => [mapping.order_id, mapping.order]));
    }, [route]);

    const stops = FTLStopHelpers.getStopSequence(route?.stopsByRouteId).filter((stop) => !stop.start && !stop.end);

    useEffect(() => {
        if (!route) return;
        const stops = FTLStopHelpers.getStopSequence(route?.stopsByRouteId).filter((stop) => !stop.start && !stop.end);
        if (!stops) return;
        const previousLunchStop = stops.find((s) => s.type === 'LUNCH');
        if (!previousLunchStop) {
            setPreviousLunchStop(null);
            setLunchDuration(null);
            setLunchStop(null);
            return;
        }

        setLunchDuration(previousLunchStop.service_time / 60000);
        setLunchStop(previousLunchStop.ordering - 1);
        setPreviousLunchStop(previousLunchStop);
    }, [route, routes]);

    const createLunchStop = () => {
        const clonedStops = cloneDeep(route.stopsByRouteId);
        const stopUpdates = clonedStops.filter((stop) => stop.ordering > lunchStop);
        const customerStops = stopUpdates.filter((stop) => FTLStopHelpers.isCustomerStop(stop, route));

        const newStop = {
            route_id: route.route_id,
            ordering: lunchStop + 1,
            type: 'LUNCH',
            start: false,
            end: false,
            service_time: lunchDuration * 60000,
        };

        const ordersThatNeedRecalculating = customerStops.reduce((acc, stop) => {
            return [...acc, ...(stop?.orders || [])];
        }, []);

        // Optimistic paint: shift the displaced stops, add the new lunch stop
        // (TEMP_STOP-filled with a temp uuid), and merge both into the full
        // patched stop set update_routes_by_pk.stopsByRouteId must carry.
        const shiftedStops = stopUpdates.map((stop) => ({
            ...TEMP_STOP,
            ...stop,
            ordering: stop.ordering + 1,
            __typename: 'stops',
        }));
        const optimisticLunchStop = { ...TEMP_STOP, ...newStop, stop_id: uuid(), __typename: 'stops' };
        const untouchedStops = clonedStops
            .filter((stop) => stop.ordering <= lunchStop)
            .map((stop) => ({ ...TEMP_STOP, ...stop, __typename: 'stops' }));
        const patchedStops = [...untouchedStops, ...shiftedStops, optimisticLunchStop].sort(
            (a, b) => a.ordering - b.ordering
        );
        const optimisticOrders = ordersThatNeedRecalculating
            .map((order_id) => ordersByKey[order_id])
            .filter(Boolean)
            .map((order) => ({ ...order, delivery_time_confirmed: 'recalculate', __typename: 'orders' }));

        updateStops({
            variables: {
                route_id: route.route_id,
                route_update: { need_to_optimize: true },
                order_updates: [
                    {
                        where: { order_id: { _in: ordersThatNeedRecalculating } },
                        _set: {
                            delivery_time_confirmed: 'recalculate',
                        },
                    },
                ],
                updates: stopUpdates.map((updatedStop) => ({
                    where: { stop_id: { _eq: updatedStop.stop_id } },
                    _set: {
                        ordering: updatedStop.ordering + 1,
                        orders: updatedStop.orders?.length ? toArrayLiteral(updatedStop.orders) : null,
                        returns: updatedStop.returns?.length ? toArrayLiteral(updatedStop.returns) : null,
                        exchanges: updatedStop.exchanges?.length ? toArrayLiteral(updatedStop.exchanges) : null,
                        ...Object.fromEntries(
                            FTLStopHelpers.stopFieldsToUpdate.map((key) => [key, updatedStop[key] || null])
                        ),
                    },
                })),
                inserts: newStop,
            },
            optimisticResponse: {
                ...buildUpdateStopsOptimistic({
                    route,
                    stops: patchedStops,
                    stopsUpdated: shiftedStops,
                    ordersUpdated: optimisticOrders,
                }),
                insert_stops: {
                    __typename: 'stops_mutation_response',
                    affected_rows: 1,
                    returning: [optimisticLunchStop],
                },
            },
            onError: (error) => {
                setError(error, 'Error adding lunch stop');
            },
        });
    };

    const updateLunchStop = () => {
        const newStops = cloneDeep(route.stopsByRouteId);

        const from = previousLunchStop.ordering;
        const to = lunchStop < from ? lunchStop + 1 : lunchStop;

        let stopsToUpdate = [];
        if (to < from) {
            newStops.forEach((stop) => {
                if (stop.ordering >= to && stop.ordering < from) {
                    stopsToUpdate.push({
                        ...stop,
                        ordering: stop.ordering + 1,
                    });
                }
            });
        }
        if (to > from) {
            newStops.forEach((stop) => {
                if (stop.ordering > from && stop.ordering <= to) {
                    stopsToUpdate.push({
                        ...stop,
                        ordering: stop.ordering - 1,
                    });
                }
            });
        }

        stopsToUpdate.push({
            ...previousLunchStop,
            ordering: to,
            service_time: lunchDuration * 60000,
        });

        const customerStops = stopsToUpdate.filter((stop) => FTLStopHelpers.isCustomerStop(stop, route));

        const ordersThatNeedRecalculating = customerStops.reduce((acc, stop) => {
            return [...acc, ...(stop?.orders || [])];
        }, []);

        // Merge the reordered/re-durationed stops into the full cached set by
        // stop_id (not a re-filter) so the optimistic stopsByRouteId carries
        // every untouched stop plus the patched ones, in ordering order.
        const optimisticStopsUpdated = stopsToUpdate.map((stop) => ({ ...TEMP_STOP, ...stop, __typename: 'stops' }));
        const updatesById = Object.fromEntries(optimisticStopsUpdated.map((stop) => [stop.stop_id, stop]));
        const patchedStops = newStops
            .map((stop) => updatesById[stop.stop_id] || { ...TEMP_STOP, ...stop, __typename: 'stops' })
            .sort((a, b) => a.ordering - b.ordering);
        const optimisticOrders = ordersThatNeedRecalculating
            .map((order_id) => ordersByKey[order_id])
            .filter(Boolean)
            .map((order) => ({ ...order, delivery_time_confirmed: 'recalculate', __typename: 'orders' }));

        updateStops({
            variables: {
                route_id: route.route_id,
                route_update: { need_to_optimize: true },
                order_updates: [
                    {
                        where: { order_id: { _in: ordersThatNeedRecalculating } },
                        _set: {
                            delivery_time_confirmed: 'recalculate',
                        },
                    },
                ],
                updates: stopsToUpdate.map((updatedStop) => ({
                    where: { stop_id: { _eq: updatedStop.stop_id } },
                    _set: {
                        ordering: updatedStop.ordering,
                        orders: updatedStop.orders?.length ? toArrayLiteral(updatedStop.orders) : null,
                        returns: updatedStop.returns?.length ? toArrayLiteral(updatedStop.returns) : null,
                        exchanges: updatedStop.exchanges?.length ? toArrayLiteral(updatedStop.exchanges) : null,
                        ...Object.fromEntries(
                            FTLStopHelpers.stopFieldsToUpdate.map((key) => [key, updatedStop[key] || null])
                        ),
                    },
                })),
            },
            optimisticResponse: buildUpdateStopsOptimistic({
                route,
                stops: patchedStops,
                stopsUpdated: optimisticStopsUpdated,
                ordersUpdated: optimisticOrders,
            }),
            onError: (error) => {
                console.log(error);
                setError(error, 'Error adding lunch stop');
            },
        });
    };

    const handleModalSave = () => {
        if (previousLunchStop) {
            updateLunchStop();
        } else {
            createLunchStop();
        }
        setLunchDuration(null);
        setLunchStop(null);
        setModalOpen(null);
    };

    const isNormalPickup = (stop) => {
        return stop.type === 'PICKUP' && !stop.returns?.length;
    };
    const isFinalDropoff = (stop) => {
        return stop.type === 'DROPOFF' && stop.returns?.length;
    };

    const prevStopNumber = previousLunchStop ? previousLunchStop.ordering : Infinity;

    return (
        <NavResponsiveModal open={modalOpen === MODALS.DRIVER_LUNCH} onClose={handleModalClose}>
            <ModalTitle
                css={css`
                    border-bottom: none;
                    font-weight: 900;
                    font-size: 20px;
                    width: 350px;
                `}
            >
                Driver Lunch
            </ModalTitle>
            <ModalContent
                css={css`
                    font-size: 16px;
                `}
            >
                <GridItemRow>
                    <span
                        css={css`
                            font-weight: 700;
                        `}
                    >
                        Lunch Duration
                    </span>
                </GridItemRow>
                <FormControl fullWidth variant="outlined">
                    <Select
                        value={lunchDuration || 'NONE'}
                        onChange={(e) => setLunchDuration(e.target.value)}
                        css={css`
                            & .MuiSelect-root {
                                padding-top: 10px;
                                padding-bottom: 10px;
                            }
                        `}
                    >
                        <MenuItem value="NONE" key="NONE">
                            Select Duration...
                        </MenuItem>
                        {lunchDurations.map((i) => (
                            <MenuItem value={i.value} key={`lunch-duration-menu-item-${i.value}`}>
                                {i.text}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <GridItemRow>
                    <span
                        css={css`
                            font-weight: 700;
                            margin-top: 40px;
                        `}
                    >
                        After
                    </span>
                </GridItemRow>
                <FormControl fullWidth variant="outlined">
                    <Select
                        value={lunchStop === null ? 'NONE' : lunchStop}
                        onChange={(e) => setLunchStop(e.target.value)}
                        css={css`
                            & .MuiSelect-root {
                                padding-top: 10px;
                                padding-bottom: 10px;
                            }
                        `}
                    >
                        <MenuItem value="NONE" key="NONE">
                            Select Stop...
                        </MenuItem>
                        {stops
                            .filter((stop) => stop.type !== 'LUNCH')
                            .map((stop) => (
                                <MenuItem value={stop.ordering} key={`lunch-stop-menu-item-${stop.ordering}`}>
                                    {`${
                                        isNormalPickup(stop)
                                            ? 'Pickup'
                                            : isFinalDropoff(stop)
                                            ? 'Final Return'
                                            : `Stop ${
                                                  stop.ordering > prevStopNumber ? stop.ordering - 1 : stop.ordering
                                              }`
                                    }`}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </ModalContent>
            <ModalActions>
                <SecondaryButton
                    css={css`
                        justify-content: center;
                        width: 128px;
                    `}
                    onClick={handleModalClose}
                >
                    Cancel
                </SecondaryButton>
                <PrimaryButton
                    css={css`
                        justify-content: center;
                        width: 128px;
                    `}
                    disabled={
                        !lunchDuration ||
                        lunchStop === null ||
                        lunchDuration === 'NONE' ||
                        lunchStop === 'NONE' ||
                        (lunchStop === previousLunchStop?.ordering - 1 &&
                            lunchDuration === previousLunchStop.service_time / 60000)
                    }
                    onClick={handleModalSave}
                >
                    Save
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default DriverLunchModal;
